import { ChartComponent } from 'ng-apexcharts';

/**
 * Monkey-patch ng-apexcharts to prevent crashes during route transitions.
 *
 * ng-apexcharts schedules chart hydration via AsapScheduler (microtask).
 * When the router destroys a component before that task completes:
 *   - createElement() tries to access a detached DOM element → "Element not found"
 *   - destroy() tries to clean up stale internal refs → "Cannot read 'node'"
 *
 * These errors poison Zone.js and block all further navigation.
 * Wrapping the critical methods with try/catch prevents the errors from
 * ever reaching Zone.js's unhandled rejection handler.
 *
 * Call this function once during app bootstrap (before any charts render).
 */
export function patchApexCharts(): void {
    const proto = ChartComponent.prototype as any;

    // Patch createElement (async — errors become rejected promises)
    const origCreate = proto.createElement;
    proto.createElement = async function (...args: any[]) {
        try {
            return await origCreate.apply(this, args);
        } catch (_) {
            // Component was destroyed mid-hydration — safe to ignore
        }
    };

    // Patch render (returns a promise from ApexCharts.render())
    const origRender = proto.render;
    proto.render = function (...args: any[]) {
        try {
            const result = origRender.apply(this, args);
            // Swallow any rejection from the inner ApexCharts.render() promise
            return result?.catch?.(() => {}) ?? result;
        } catch (_) {
            // Sync throw — element already gone
        }
    };

    // Patch destroy (sync but accesses stale DOM)
    const origDestroy = proto.destroy;
    proto.destroy = function (...args: any[]) {
        try {
            return origDestroy.apply(this, args);
        } catch (_) {
            // Internal refs already cleaned up or DOM detached
        }
    };
}
